import { SERVICE_BASE_URLS } from "../global/constants";
import api from "./api";
import {
  ISidekickPlantsResponse,
  ISidekickTurnsQuery,
  ISidekickTurnsResponse,
} from "../types/api/sidekick-usage.type";

const ADMIN_ROOT = `${SERVICE_BASE_URLS.AGENT}/api/admin`;

/**
 * Build the query string shared by /turns and /turns.csv. Only filters that
 * narrow the result are sent; defaults (asker=all, empty lists, blank text)
 * are left out so equivalent requests produce identical URLs.
 */
export const buildTurnsParams = (
  query: ISidekickTurnsQuery,
  { includePaging }: { includePaging: boolean }
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("env_ids", query.envIds.join(","));
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.users.length) params.set("users", query.users.join(","));
  if (query.asker && query.asker !== "all") params.set("asker", query.asker);
  if (query.rating.length) params.set("rating", query.rating.join(","));
  if (query.hasComment) params.set("has_comment", query.hasComment);
  if (query.flags.length) params.set("flags", query.flags.join(","));
  if (query.client) params.set("client", query.client);
  if (query.model.trim()) params.set("model", query.model.trim());
  if (query.q.trim()) params.set("q", query.q.trim());
  if (includePaging) {
    params.set("page", String(query.page));
    params.set("page_size", String(query.pageSize));
  }
  return params;
};

const getPlants = () => {
  return api.get<ISidekickPlantsResponse>(`${ADMIN_ROOT}/plants`);
};

const getTurns = (query: ISidekickTurnsQuery) => {
  const params = buildTurnsParams(query, { includePaging: true });
  return api.get<ISidekickTurnsResponse>(`${ADMIN_ROOT}/turns?${params}`);
};

/** Raised by exportTurnsCsv with a message that can be shown as-is. */
export class CsvExportError extends Error {
  rows?: number;
  limit?: number;
  constructor(message: string, rows?: number, limit?: number) {
    super(message);
    this.name = "CsvExportError";
    this.rows = rows;
    this.limit = limit;
  }
}

const readBlobText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * The api.ts response interceptor rejects with `error.response.data` when there
 * is a response. With responseType "blob" that is a Blob holding the JSON error
 * body, so read it back before deciding what to tell the user.
 */
const toCsvExportError = async (error: unknown): Promise<CsvExportError> => {
  let body: any = error;
  if (typeof Blob !== "undefined" && error instanceof Blob) {
    try {
      body = JSON.parse(await readBlobText(error));
    } catch {
      body = undefined;
    }
  }
  if (body && body.error === "too_many_rows") {
    const rows = Number(body.rows);
    const limit = Number(body.limit);
    return new CsvExportError(
      `Too many rows (${rows.toLocaleString("en-US")}) — narrow the time window`,
      rows,
      limit
    );
  }
  if (body && typeof body.error === "string") {
    return new CsvExportError(`Export failed: ${body.error}`);
  }
  return new CsvExportError("Export failed. Please try again.");
};

/**
 * Fetch turns.csv with the bearer token (through the shared api instance) and
 * return it as a Blob. Throws CsvExportError on failure.
 */
const exportTurnsCsv = async (query: ISidekickTurnsQuery): Promise<Blob> => {
  const params = buildTurnsParams(query, { includePaging: false });
  try {
    const response = await api.get<Blob>(`${ADMIN_ROOT}/turns.csv?${params}`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw await toCsvExportError(error);
  }
};

/** Hand a Blob to the browser as a file download. */
export const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const SidekickUsageService = {
  getPlants,
  getTurns,
  exportTurnsCsv,
};

export default SidekickUsageService;
